import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
} from "@mui/material";
import { AttachFile } from "@mui/icons-material";

const Campaigns = () => {
  const { t } = useTranslation("settings");
  const [campaigns, setCampaigns] = useState([]);
  const [newCampaign, setNewCampaign] = useState({
    name: "",
    type: "whatsapp",
    segment: "all",
    message: "",
    media: null,
    schedule: false,
    date: "",
    time: "",
  });

  const handleCreateCampaign = () => {
    if (!newCampaign.name || !newCampaign.message || !newCampaign.type) {
      alert(t("campaignsPage.validationRequired"));
      return;
    }

    setCampaigns([
      ...campaigns,
      {
        ...newCampaign,
        id: campaigns.length + 1,
        status: newCampaign.schedule
          ? t("campaignsPage.status.scheduled")
          : t("campaignsPage.status.sent"),
      },
    ]);

    setNewCampaign({
      name: "",
      type: "whatsapp",
      segment: "all",
      message: "",
      media: null,
      schedule: false,
      date: "",
      time: "",
    });
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    setNewCampaign({ ...newCampaign, media: file });
  };

  return (
    <Box p={4}>
      <Typography variant="h4" mb={3}>
        {t("campaignsPage.title")}
      </Typography>

      <Box mb={3}>
        <Typography variant="h6" gutterBottom>
          {t("campaignsPage.createSection")}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label={t("campaignsPage.campaignName")}
              fullWidth
              value={newCampaign.name}
              onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t("campaignsPage.channelType")}</InputLabel>
              <Select
                label={t("campaignsPage.channelType")}
                value={newCampaign.type}
                onChange={(e) => setNewCampaign({ ...newCampaign, type: e.target.value })}
              >
                <MenuItem value="whatsapp">{t("campaignsPage.channels.whatsapp")}</MenuItem>
                <MenuItem value="sms">{t("campaignsPage.channels.sms")}</MenuItem>
                <MenuItem value="email">{t("campaignsPage.channels.email")}</MenuItem>
                <MenuItem value="calls">{t("campaignsPage.channels.calls")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t("campaignsPage.segmentation")}</InputLabel>
              <Select
                label={t("campaignsPage.segmentation")}
                value={newCampaign.segment}
                onChange={(e) => setNewCampaign({ ...newCampaign, segment: e.target.value })}
              >
                <MenuItem value="all">{t("campaignsPage.segments.all")}</MenuItem>
                <MenuItem value="active">{t("campaignsPage.segments.active")}</MenuItem>
                <MenuItem value="inactive">{t("campaignsPage.segments.inactive")}</MenuItem>
                <MenuItem value="custom">{t("campaignsPage.segments.custom")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t("campaignsPage.message")}
              fullWidth
              multiline
              rows={4}
              value={newCampaign.message}
              onChange={(e) => setNewCampaign({ ...newCampaign, message: e.target.value })}
              placeholder={t("campaignsPage.messagePlaceholder", {
                type: t(`campaignsPage.channels.${newCampaign.type}`),
              })}
            />
          </Grid>

          {(newCampaign.type === "whatsapp" || newCampaign.type === "email") && (
            <Grid item xs={12}>
              <Button variant="outlined" component="label" startIcon={<AttachFile />}>
                {t("campaignsPage.attachMedia")}
                <input type="file" hidden onChange={handleFileUpload} />
              </Button>
              {newCampaign.media && (
                <Typography variant="body2" mt={1}>
                  {t("campaignsPage.selectedFile", { name: newCampaign.media.name })}
                </Typography>
              )}
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={newCampaign.schedule}
                  onChange={(e) => setNewCampaign({ ...newCampaign, schedule: e.target.checked })}
                />
              }
              label={t("campaignsPage.scheduleSend")}
            />
          </Grid>
          {newCampaign.schedule && (
            <>
              <Grid item xs={6} md={3}>
                <TextField
                  label={t("campaignsPage.date")}
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={newCampaign.date}
                  onChange={(e) => setNewCampaign({ ...newCampaign, date: e.target.value })}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  label={t("campaignsPage.time")}
                  type="time"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={newCampaign.time}
                  onChange={(e) => setNewCampaign({ ...newCampaign, time: e.target.value })}
                />
              </Grid>
            </>
          )}
        </Grid>
        <Button variant="contained" color="primary" onClick={handleCreateCampaign} sx={{ mt: 2 }}>
          {t("campaignsPage.createButton")}
        </Button>
      </Box>

      <Divider />

      <Box mt={3}>
        <Typography variant="h6" gutterBottom>
          {t("campaignsPage.listSection")}
        </Typography>
        <Grid container spacing={3}>
          {campaigns.map((campaign) => (
            <Grid item xs={12} md={6} key={campaign.id}>
              <Card>
                <CardContent>
                  <Typography variant="h6">{campaign.name}</Typography>
                  <Typography>
                    {t("campaignsPage.channelLabel")}{" "}
                    {t(`campaignsPage.channels.${campaign.type}`, { defaultValue: campaign.type })}
                  </Typography>
                  <Typography>
                    {t("campaignsPage.segmentLabel")}{" "}
                    {t(`campaignsPage.segments.${campaign.segment}`, { defaultValue: campaign.segment })}
                  </Typography>
                  <Typography>
                    {t("campaignsPage.statusLabel")} {campaign.status}
                  </Typography>
                  {campaign.schedule && (
                    <Typography>
                      {t("campaignsPage.scheduledFor", { date: campaign.date, time: campaign.time })}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default Campaigns;
